import { t } from 'i18next';
import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import { Card, Col, Row } from 'react-bootstrap';
import HEADER_TOOLBAR from '../HEADER_TOOLBAR';
import { ActionButton, IconButton, TextField } from '@fluentui/react';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import './style.css';

const processChartData = (rawItems) => {
  if (!rawItems || rawItems.length === 0) {
    return {
      categories: [],
      quantities: [],
      percentages: [],
      detalleItems: [],
      totalGeneral: 0
    };
  }

  const sortedItems = [...rawItems].sort((a, b) => {
    return (b.total || 0) - (a.total || 0);
  });

  const totalGeneral = sortedItems.reduce((sum, item) => {
    return sum + (item.total || 0);
  }, 0);

  const top5Items = sortedItems.slice(0, 5);
  const otrosItems = sortedItems.slice(5);

  const totalOtros = otrosItems.reduce((sum, item) => {
    return sum + (item.total || 0);
  }, 0);

  const categories = top5Items.map(item => item.name);
  const quantities = top5Items.map(item => item.total || 0);

  if (otrosItems.length > 0) {
    categories.push(`Otros (${otrosItems.length} cat.)`);
    quantities.push(totalOtros);
  }

  const percentages = quantities.map(qty => {
    if (totalGeneral === 0) {
      return 0;
    }

    return Number(((qty / totalGeneral) * 100).toFixed(1));
  });

  return {
    categories,
    quantities,
    percentages,
    detalleItems: sortedItems,
    totalGeneral
  };
};

const buildChartOptions = (categories, quantities) => {
  return {
    chart: {
      id: 'contract-category-chart',
      type: 'bar',
      height: '100%',
      width: '100%',
      toolbar: {
        show: false
      },
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      zoom: {
        enabled: false
      },
      selection: {
        enabled: false
      }
    },

    parentHeightOffset: 0,

    tooltip: {
      enabled: true,
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        const category = categories?.[dataPointIndex] ?? '';
        const percentage = series?.[seriesIndex]?.[dataPointIndex] ?? 0;
        const quantity = quantities?.[dataPointIndex] ?? 0;

        return `
          <div style="
            padding: 8px 10px;
            font-family: Segoe UI, sans-serif;
            font-size: 12px;
          ">
            <div style="font-weight: 600; margin-bottom: 4px;">
              <b>${category}</b>
            </div>

            <div>
              <b>Peso sobre el total</b>: ${Number(percentage).toFixed(1)}%
            </div>

            <div>
              <b>Cantidad de contratos</b>: ${quantity}
            </div>
          </div>
        `;
      }
    },

    plotOptions: {
      bar: {
        columnWidth: '55%',
        dataLabels: {
          position: 'top'
        }
      }
    },

    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return `${Number(val).toFixed(1)}%`;
      },
      style: {
        fontSize: '11px',
        fontFamily: 'Segoe UI',
        fontWeight: '600',
        colors: ['#333333']
      },
      textAnchor: 'middle',
      background: {
        enabled: false
      },
      offsetY: -16
    },

    labels: categories,

    xaxis: {
      type: 'category',
      labels: {
        rotate: -45,
        trim: true,
        style: {
          fontSize: '12px'
        }
      }
    },

    yaxis: {
      min: 0,
      forceNiceScale: true,
      title: {
        text: 'Peso sobre el Total'
      },
      labels: {
        formatter: val => `${Number(val).toFixed(0)}%`
      }
    },

    colors: ['#008FFB'],

    legend: {
      show: false
    }
  };
};

const ELEMENT_CONTRACT_CATEGORY = ({
  inputParam_HideHeader,
  inputParam_ComponentContent,
  inputParam_UseFake,
  inputParam_BoardStructure,
  inputParam_Container
}) => {
  const chartRef = useRef(null);
  const [mostrarDrilldown, setMostrarDrilldown] = useState(false);
  const [searchText, setSearchText] = useState('');

  const rawData = useMemo(() => {
    return inputParam_ComponentContent?.items || [];
  }, [inputParam_ComponentContent?.items]);

  const chartData = useMemo(() => {
    return processChartData(rawData);
  }, [rawData]);

  const {
    categories,
    quantities,
    percentages,
    detalleItems,
    totalGeneral
  } = chartData;

  const series = useMemo(() => {
    return [
      {
        name: 'Peso sobre el Total',
        data: percentages
      }
    ];
  }, [percentages]);

  const options = useMemo(() => {
    return buildChartOptions(categories, quantities);
  }, [categories, quantities]);

  const title = useMemo(() => {
    return (!inputParam_UseFake && inputParam_ComponentContent !== null)
      ? (
          inputParam_ComponentContent?.boardComponentTitle ??
          t('data-lang-boxname-portfoliocategory')
        )
      : t('data-lang-boxname-portfoliocategory');
  }, [inputParam_UseFake, inputParam_ComponentContent]);

  const hasDetailData = detalleItems && detalleItems.length > 0;

  const filteredDetalleItems = useMemo(() => {
    const text = (searchText || '').trim().toLowerCase();

    if (!text) {
      return detalleItems || [];
    }

    return (detalleItems || []).filter(item =>
      (item.name || '').toLowerCase().includes(text)
    );
  }, [detalleItems, searchText]);

  const handleOpenDrilldown = () => {
    if (!hasDetailData) {
      return;
    }

    setSearchText('');
    setMostrarDrilldown(true);
  };

  const handleCloseDrilldown = () => {
    setSearchText('');
    setMostrarDrilldown(false);
  };

  const renderContent = () => {
    return (
      <div
        className="h-100 w-100 position-relative"
        style={{
          opacity: inputParam_UseFake ? '.4' : '',
          overflow: 'hidden'
        }}
      >
        <Row className="px-3 h-100 m-0">
          <Col
            className="contract-category-chart"
            style={{
              minHeight: '300px',
              height: '100%'
            }}
          >
            <ReactApexChart
              ref={chartRef}
              options={options}
              series={series}
              type="bar"
              height="100%"
              width="100%"
            />
          </Col>
        </Row>

        {
          mostrarDrilldown &&
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: '55%',
              minWidth: '360px',
              maxWidth: '650px',
              background: 'rgba(255, 255, 255, 0.98)',
              borderLeft: '1px solid #ddd',
              boxShadow: '-4px 0 12px rgba(0,0,0,0.18)',
              zIndex: 20,
              padding: '12px 16px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              style={{
                flex: '0 0 auto',
                background: 'rgba(255, 255, 255, 0.98)',
                zIndex: 2
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="m-0">
                  <b>Detalle de Categorías</b>
                </h6>

                <ActionButton
                  iconProps={{ iconName: 'Cancel' }}
                  onClick={handleCloseDrilldown}
                />
              </div>

              <TextField
                placeholder="Buscar categoría..."
                value={searchText}
                onChange={(event, value) => setSearchText(value || '')}
                iconProps={{ iconName: 'Search' }}
                styles={{
                  root: {
                    marginBottom: '12px'
                  },
                  fieldGroup: {
                    height: '30px'
                  },
                  field: {
                    fontSize: '12px'
                  }
                }}
              />
            </div>

            <div
              style={{
                flex: '1 1 auto',
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingRight: '4px'
              }}
            >
              {
                filteredDetalleItems && filteredDetalleItems.length > 0 &&
                filteredDetalleItems.map((item, index) => {
                  const total = item.total || 0;

                  const ratio = totalGeneral > 0
                    ? total / totalGeneral
                    : 0;

                  return (
                    <div key={index} className="mb-3">
                      <div
                        className="d-flex justify-content-between style-text"
                        style={{
                          fontSize: '12px',
                          gap: '12px'
                        }}
                      >
                        <span>{item.name}</span>

                        <b className="text-nowrap">
                          {total} {total === 1 ? 'contrato' : 'contratos'} ({(ratio * 100).toFixed(1)}%)
                        </b>
                      </div>

                      <ProgressIndicator
                        percentComplete={ratio}
                        barHeight={6}
                        styles={{
                          itemProgress: {
                            chartColor: '#00E396'
                          }
                        }}
                      />
                    </div>
                  );
                })
              }

              {
                filteredDetalleItems && filteredDetalleItems.length === 0 &&
                <div className="text-muted" style={{ fontSize: '12px' }}>
                  No se encontraron categorías para la búsqueda ingresada.
                </div>
              }
            </div>
          </div>
        }
      </div>
    );
  };

  return (
    <>
      {
        !inputParam_UseFake &&
        <Card className="h-100 d-flex flex-column">
          {
            !inputParam_HideHeader &&
            <Card.Header className="card-header-compact">
              <Card.Title className="d-flex justify-content-between">
                <b>{title}</b>

                <div className="toolbar d-flex align-items-center">
                  <IconButton
                    iconProps={{ iconName: 'BulletedList' }}
                    title="Ver detalle de categorías"
                    ariaLabel="Ver detalle de categorías"
                    disabled={!hasDetailData}
                    onClick={handleOpenDrilldown}
                  />

                  <HEADER_TOOLBAR
                    inputParam_BoardStructure={inputParam_BoardStructure}
                    inputParam_Container={inputParam_Container}
                  />
                </div>
              </Card.Title>
            </Card.Header>
          }

          <Card.Body
            className="px-1"
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {renderContent()}
          </Card.Body>
        </Card>
      }

      {
        inputParam_UseFake &&
        renderContent()
      }
    </>
  );
};

ELEMENT_CONTRACT_CATEGORY.propTypes = {
  inputParam_HideHeader: PropTypes.bool,
  inputParam_ComponentContent: PropTypes.object,
  inputParam_UseFake: PropTypes.bool,
  inputParam_BoardStructure: PropTypes.object,
  inputParam_Container: PropTypes.object
};

ELEMENT_CONTRACT_CATEGORY.defaultProps = {
  inputParam_HideHeader: false,
  inputParam_ComponentContent: null,
  inputParam_UseFake: false
};

export default ELEMENT_CONTRACT_CATEGORY;